import React, { useEffect, lazy, Suspense, useMemo } from "react";
import { sortBy, take, pipe, isNil } from "ramda";
import { Index } from "react-instantsearch";

import {
  usePills,
  useLoading,
  useHomePage,
  useRouter,
  useGtm,
  useAlgolia,
  useSettings,
  usePageModules,
  useUsers,
} from "hooks";
import PillPresenter from "presenters/PillPresenter";
import MetaTags from "components/MetaTags";
import MainTitle from "components/MainTitle";
import LDJsonSchema from "components/LDJsonSchema";
import logoVertical from "assets/images/common/logo-vertical.png";
import UpcomingDates from "components/UpcomingDates";
import AlgoliaSearchWrapper from "components/AlgoliaSearchWrapper";

import AlgoliaFeaturedEcards from "./components/AlgoliaFeaturedEcards";
import AlgoliaFeaturedClips from "./components/AlgoliaFeaturedClips";
import useStyles from "./useStyles";
import LoadingSkeleton from "./components/LoadingSkeleton";

const Hero = lazy(() => import("./components/Hero"));
const FeaturedCategories = lazy(() => import("./components/FeaturedCategories"));
const Categories = lazy(() => import("components/Categories"));

const Home = () => {
  const classes = useStyles();
  const { pathname } = useRouter();
  const { origin } = window.location;
  const url = `${origin}${pathname}`;
  const { trackVirtualPageView } = useGtm();
  const { loadHomePage, featuredEcards, carousel } = useHomePage();
  const { loadPills, pills } = usePills();
  const featuredCategories = pills && pipe(sortBy(PillPresenter.rank), take(4))(pills);
  const { isNotFinished: isPendingHomePage, func: loadHomePageWithLoading } = useLoading(loadHomePage);
  const { isNotFinished: isPendingPills, func: loadPillsWithLoading } = useLoading(loadPills);
  const { createInitialUiStateFromMultiple } = useAlgolia();
  const { getPageModulesConfiguration } = usePageModules();
  const {
    settings: { algolia },
  } = useSettings();
  const { currentUserLoaded } = useUsers();
  const initialUiState = useMemo(() => {
    if (isNil(featuredEcards) || isNil(carousel) || !currentUserLoaded) return undefined;

    return createInitialUiStateFromMultiple(getPageModulesConfiguration([featuredEcards, carousel]));
  }, [featuredEcards, carousel, getPageModulesConfiguration, currentUserLoaded]);

  const organizationSchema = {
    "@context": "http://schema.org",
    "@type": "Organization",
    url: "https://www.jibjab.com/",
    logo: `https://www.jibjab.com${logoVertical}`,
    sameAs: ["https://twitter.com/jibjab", "https://www.facebook.com/jibjab"],
  };

  useEffect(() => {
    Promise.all([loadHomePageWithLoading(), loadPillsWithLoading()]);
    trackVirtualPageView({ name: "homepage" });
  }, []);

  if (isPendingHomePage || !currentUserLoaded) return <LoadingSkeleton />;

  return (
    <div className={classes.root}>
      <AlgoliaSearchWrapper context="home" initialUiState={initialUiState}>
        <Index indexName={algolia.indexName} indexId={carousel?.id}>
          <AlgoliaFeaturedClips algoliaCard={carousel} initialQueryObject={initialUiState?.[carousel?.id]} />
        </Index>
        <Index indexName={algolia.indexName} indexId={featuredEcards?.id}>
          <AlgoliaFeaturedEcards
            algoliaCard={featuredEcards}
            initialQueryObject={initialUiState?.[featuredEcards?.id]}
          />
        </Index>
        <UpcomingDates context="home" />
        <Suspense fallback={<span />}>
          <Hero />
          <FeaturedCategories items={featuredCategories} loading={isPendingPills} />
          <Categories />
        </Suspense>
      </AlgoliaSearchWrapper>
      <LDJsonSchema schema={organizationSchema} />
      <MainTitle title="eCards - Customize & Send Funny Greeting Cards Online - JibJab" />
      <MetaTags>
        <meta
          name="keywords"
          content="Jibjab Ecards, ecards, greetings, happy birthday, birthday ecards, holiday ecards, anniversary, funny egreetings, video ecards, funny ecard, free ecards, free e cards, funny ecards"
        />
        <meta
          name="description"
          content="Personalized eCards from JibJab for every occasion. Browse, customize, send funny greeting cards online for birthdays, holidays, valentines, thank yous &amp; more!"
        />
        <meta property="og:site_name" content="JibJab" />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Funny eCards - Send Custom Greeting Cards Online w/ JibJab!" />
        <meta
          property="og:description"
          content="Personalized eCards from JibJab for every occasion. Browse, customize, send funny greeting cards online for birthdays, holidays, valentines, thank yous &amp; more!"
        />
      </MetaTags>
    </div>
  );
};

export default Home;
